import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ethers } from 'ethers';
import { Wallet, ExternalLink, Link2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

export const buyUrl = (amount) => (isMobile() || !window.ethereum
  ? `https://link.metamask.io/buy?chainId=1&amount=${amount}`
  : `https://portfolio.metamask.io/buy/build-quote?chainId=1&amount=${amount}`);

export const MetaMaskBuyPanel = ({ usd, linked, token, onLinked }) => {
  const [address, setAddress] = useState(null);
  const [balance, setBalance] = useState(null);
  const [busy, setBusy] = useState(false);

  const readBalance = async (addr) => {
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      setBalance(ethers.utils.formatEther(await provider.getBalance(addr)));
    } catch { setBalance(null); }
  };

  useEffect(() => {
    if (!window.ethereum) return;
    window.ethereum.request({ method: 'eth_accounts' }).then(a => { if (a[0]) { setAddress(a[0]); readBalance(a[0]); } }).catch(() => {});
  }, []);

  const connect = async () => {
    if (!window.ethereum) { window.open(buyUrl(usd || 50), '_blank'); return; }
    setBusy(true);
    try {
      const [addr] = await window.ethereum.request({ method: 'eth_requestAccounts' });
      await window.ethereum.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: '0x1' }] }).catch(() => {});
      setAddress(addr); readBalance(addr);
      toast.success('MetaMask connected on Ethereum mainnet');
    } catch (e) { toast.error(e.code === 4001 ? 'Connection rejected in MetaMask' : 'Could not connect MetaMask'); }
    finally { setBusy(false); }
  };

  const link = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/pas-web3auth/link-wallet`, { address }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(r.data.message); onLinked?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not link wallet'); }
    finally { setBusy(false); }
  };

  const isLinked = address && linked?.address?.toLowerCase() === address.toLowerCase();
  return (
    <div className="mt-6 rounded-lg border border-orange-500/30 bg-orange-500/5 p-4" data-testid="metamask-buy-panel">
      <p className="text-sm text-orange-200 font-semibold flex items-center gap-2"><Wallet className="w-4 h-4" /> Already use MetaMask? Buy ETH there</p>
      <p className="text-xs text-slate-400 mt-1">Optional. Pay on MetaMask's buy page; ETH arrives in your own MetaMask wallet on Ethereum mainnet.</p>
      {address ? (
        <p className="text-xs text-slate-300 mt-2" data-testid="metamask-connected-address">
          Connected: <span className="font-mono text-orange-300">{address.slice(0, 6)}…{address.slice(-4)}</span>
          {balance !== null && <span data-testid="metamask-eth-balance"> · {Number(balance).toFixed(6)} ETH</span>}
        </p>
      ) : !window.ethereum && <p className="text-xs text-amber-300 mt-2" data-testid="metamask-not-installed">MetaMask not detected in this browser. The button below opens MetaMask's buy page (mobile app or download).</p>}
      <div className="flex flex-wrap gap-3 mt-3">
        {!address && window.ethereum && (
          <button onClick={connect} disabled={busy} data-testid="metamask-connect-btn"
            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white rounded-lg text-sm flex items-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />} Connect MetaMask
          </button>
        )}
        <a href={buyUrl(usd || 50)} target="_blank" rel="noreferrer" data-testid="metamask-buy-btn"
          className="px-4 py-2 border border-orange-500 text-orange-200 hover:bg-orange-500/10 rounded-lg text-sm flex items-center gap-2">
          <ExternalLink className="w-4 h-4" /> Buy ${usd || 50} of ETH in MetaMask
        </a>
        {address && !isLinked && (
          <button onClick={link} disabled={busy} data-testid="metamask-link-btn"
            className="px-4 py-2 border border-emerald-500 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-40 rounded-lg text-sm flex items-center gap-2">
            <Link2 className="w-4 h-4" /> Use this MetaMask for reserve deliveries
          </button>
        )}
        {isLinked && <span className="text-xs text-emerald-400 self-center" data-testid="metamask-linked-badge">Linked as delivery wallet</span>}
      </div>
    </div>
  );
};
